import React, { useEffect, useState, useRef } from 'react';
import './CompassStateBar.css';

const CompassStateBar = ({ compassState }) => {
  const [rotation, setRotation] = useState(0);
  const motorGearRef = useRef(null);

  useEffect(() => {
    if (!compassState) return;

    const MOTOR_PROPORTION = 0.5; // Adjust this value as needed
    let lastTime = Date.now();
    let animationFrameId;

    const updateRotation = () => {
      const now = Date.now();
      const elapsed = now - lastTime;
      lastTime = now;

      if (motorGearRef.current) {
        const speed = compassState.motorSpeed || 0;
        const increment = (speed - 90) * MOTOR_PROPORTION * (elapsed / 1000);
        setRotation((prevRotation) => prevRotation + increment);
      }

      animationFrameId = requestAnimationFrame(updateRotation);
    };

    updateRotation();

    return () => cancelAnimationFrame(animationFrameId);
  }, [compassState]);

  if (!compassState) {
    return null;
  }

  const getBatteryStyle = () => {
    if (compassState.powerOff) return 'red';
    if (compassState.lowPower) return 'yellow';
    return 'green';
  };

  const getTemperatureDisplay = () => {
    const temp = compassState.temperature;
    if (temp < -50 || temp > 150) {
      return (
        <span className="crossed-out" style={{ color: 'red' }}>
          🌡️
        </span>
      );
    }
    return `🌡️ ${temp}°C`;
  };

  const formatDistance = (distance) => {
    if (distance >= 1000) {
      return `${Math.floor(distance / 1000)} km ${distance % 1000} m`;
    }
    return `${distance} m`;
  };

  const spinSpeedRotation = (speed) => {
    return { transform: `rotate(${speed * 0.5}deg)` };
  };

  const formatNow = (value) => {
    if (!value || value === 0) {
      return (
        <span className="crossed-out" style={{ color: 'red' }}>
          ⏰
        </span>
      );
    }
    const year = value.toString().slice(0, 2);
    const month = value.toString().slice(2, 4);
    const day = value.toString().slice(4, 6);
    const hour = value.toString().slice(6, 8);
    const minute = value.toString().slice(8, 10);
    const date = new Date(`20${year}-${month}-${day}T${hour}:${minute}`);
    return `🕰️ ${date.toLocaleString('en-US', { month: 'short', day: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: false })}`;
  };

  return (
    <div className="compass-state-bar">
      <div className="top-row">
        <div className="indicator closed">
          {compassState.closed ? '🔒' : '🔓'}
        </div>
        <div className="indicator light">
          <span style={{ opacity: compassState.enableUVLED ? 1 : 0.3 }}>💡</span>
        </div>
        <div className="indicator motor">
          <span ref={motorGearRef} className={`motor-gear ${compassState.disableMotor ? 'crossed-out' : ''}`} style={{ transform: `rotate(${rotation}deg)` }}>⚙️</span>
          {compassState.spinMotor && <span style={spinSpeedRotation(compassState.spinSpeed)}>🔄</span>}
          <span>
            {compassState.motorSpeed}
            {compassState.calibrate && ' 🪛'}
            {compassState.fixDialPosition > -1 && ` 🔨 ${compassState.fixDialPosition}`}
          </span>
        </div>
        <div className="indicator temperature">
          {getTemperatureDisplay()}
        </div>
        <div className="indicator battery">
          <span className="battery-icon" title={`${compassState.batteryLevel}% (${compassState.batteryVoltage ? compassState.batteryVoltage.toFixed(2) : 0}V)`}>🔋</span>
          <div className="battery-bar">
            <div className="battery-fill" style={{ width: `${compassState.batteryLevel}%`, backgroundColor: getBatteryStyle() }}></div>
            <div className="battery-text">
              {compassState.batteryLevel}% ({compassState.batteryVoltage ? compassState.batteryVoltage.toFixed(2) : 0}V)
            </div>
          </div>
        </div>
      </div>
      <div className="bottom-row">
        <div className="indicator position destination">
          <span style={{ textDecoration: compassState.havePosition ? 'none' : 'line-through', color: compassState.havePosition ? 'inherit' : 'red' }}>🛰️</span>
          📍 {compassState.destination ? compassState.destination.name : 'no destination'}: {formatDistance(compassState.distance)}
        </div>
        <div className="indicator time">
          {formatNow(compassState.now)}
        </div>
      </div>
    </div>
  );
};

export default CompassStateBar;
